import { useState } from 'react';
import PropTypes from 'prop-types';
import { CaretDown } from '@phosphor-icons/react/dist/ssr/CaretDown';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';
import { TYPOGRAPHY, BORDER_RADIUS } from '../../styles/UISystem';
import {
  formatDateTimeForDisplay,
  formatLocationForAnon,
  deriveMetPlaceParts,
  formatMetPlace,
} from '../../utils/locationFormatters';
import {
  deleteCustomVenue,
  saveCustomVenue,
} from '../../services/location/providers/radarProvider';

/**
 * VenueSelectorMessage Component
 *
 * Extends the system message style (centered pill) with interactive venue selection.
 * Profile users can tap to expand dropdown and select a different venue.
 * Anon users see static display (no chevron, no interaction).
 *
 * Props:
 * - connectionMetadata: full metadata object with locationData.venueInfo.nearbyVenues
 * - isEditable: true for Profile user, false for Anon
 * - onVenueChange: callback when Profile selects different venue
 * - onSystemClick: handler for clicking (navigates to detail page when not editable)
 * - dropDirection: 'down' (default) or 'up' - direction dropdown expands
 * - backgroundColor: pill background color (default '#F9F9FB')
 * - maxDropdownHeight: max height in pixels for scrollable venue list (default 200)
 * - maxWidth: max width in pixels for the pill (default 340)
 * - keepOpenOnSelect: if true, dropdown stays open after selecting a venue (default false)
 * - isAnonView: if true, shows city-only location for privacy (default false)
 * - showAddPlaceInvite: show the "Add place" invite in the no-location state even
 *   when not inline-editable — for surfaces where a wrapper tap opens LocationOverlay
 *   (detail-page WE MET cards). Inline-editable pills show it automatically.
 */
const VenueSelectorMessage = ({
  connectionMetadata,
  isEditable = false,
  onVenueChange,
  onSystemClick,
  onExpandChange,
  dropDirection = 'down',
  backgroundColor = '#F9F9FB',
  maxDropdownHeight = 200,
  maxWidth = 340,
  keepOpenOnSelect = false,
  showDateTime = true,
  inlineDropdown = false,
  isAnonView = false,
  showAddPlaceInvite = false,
  // Rounded half-hour time, no colon ("2pm" / "230pm") — the Anon detail WE
  // MET card only; every other surface keeps exact time
  friendlyTime = false,
  // Hide the street-address line (Anon detail card, decision 2026-07-19):
  // neighborhood-or-venue is the memory hook; street detail is offputting to
  // a fresh Anon. Conversion reveals the full record via the Profile pages.
  showAddress = true,
  // The detail cards drop the pin (2026-08-18 frames); the chat pill keeps it.
  showPin = true,
  // Detail cards name the place as "venue in neighbourhood". Without the area a
  // bare venue name doesn't identify anywhere — there are several Copper Mug
  // Coffees (user, 2026-08-19). Especially load-bearing now the street address
  // is gone.
  composePlace = false,
  // Style overrides for the primary place line — the Anon detail card
  // standardizes it to the Hometown line's 16px/400 (the chat pill keeps its
  // own scale)
  primaryTextStyle = null,
  align = 'center',
  // flush: drop the pill's vertical padding (keep horizontal for left-alignment).
  // Used by the Anon detail page where the pill is transparent (no card), so the
  // venue sits tight under the WE MET header like the other flat sections.
  flush = false,
}) => {
  const dropUp = dropDirection === 'up';
  // Content alignment — 'center' for the chat system-message pill, 'left' for the
  // detail-page WE MET cards (which sit in a left-aligned page).
  const isLeft = align === 'left';
  const contentJustify = isLeft ? 'flex-start' : 'center';
  const contentTextAlign = isLeft ? 'left' : 'center';
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const [customVenueName, setCustomVenueName] = useState('');
  const [deletedVenues, setDeletedVenues] = useState([]);

  // Extract venue data from connectionMetadata
  const venueInfo = connectionMetadata?.locationData?.venueInfo || {};
  const nearbyVenues = venueInfo.nearbyVenues || [];
  const currentVenue = venueInfo.venue || null;
  const coords = connectionMetadata?.locationData?.coords || {};

  // Place lines come from the ONE shared derivation (deriveMetPlaceParts) —
  // the saved-contact note mirrors this card by consuming the same helper,
  // so the two can never drift. Venue name leads when present; otherwise the
  // chat-pill place line leads (neighborhood → city) and the address drops
  // its duplicated component. Degraded geocode keeps address-as-primary.
  const derived = deriveMetPlaceParts(connectionMetadata);
  const { subordinate, fullAddress } = derived;
  const primaryPlace = composePlace
    ? formatMetPlace(connectionMetadata) || derived.primaryPlace
    : derived.primaryPlace;

  // Shared style for every primary place/location line (venue, no-venue place,
  // anon city, fallbacks) — 16px on the detail page (container BODY_REGULAR is 20px)
  const primaryLineStyle = {
    fontWeight: '500',
    color: '#333',
    fontSize: isLeft ? '16px' : undefined,
    display: 'flex',
    alignItems: 'center',
    justifyContent: contentJustify,
    gap: '4px',
    ...(primaryTextStyle || {}),
  };
  const placePin = showPin ? <MapPin size={16} color="#888" weight="regular" /> : null;

  // Handle deleting a custom venue
  const handleDeleteCustomVenue = (venueName) => {
    if (coords.latitude && coords.longitude) {
      deleteCustomVenue(venueName, coords.latitude, coords.longitude);
      // Remove from UI immediately
      setDeletedVenues((prev) => [...prev, venueName.toLowerCase()]);
      console.log('[VenueSelectorMessage] Deleted custom venue:', venueName);
    }
  };

  // Show chevron when interactive (either editable or has click handler)
  const canExpand = isEditable;
  const showChevron = isEditable || onSystemClick;

  // Format date/time line — always the full date, pinned to the meeting's local
  // wall clock when an offset was captured (the canonical record of the meeting)
  const dateTimeLine = formatDateTimeForDisplay(
    connectionMetadata?.meetingDate,
    connectionMetadata?.meetingUtcOffset,
    { friendlyTime }
  );

  // Handle click on the pill
  const handlePillClick = (e) => {
    if (canExpand) {
      e.stopPropagation();
      const nextExpanded = !isExpanded;
      setIsExpanded(nextExpanded);
      if (onExpandChange) onExpandChange(nextExpanded);
      // Reset inputs when closing dropdown
      if (!nextExpanded) {
        setIsAddingCustom(false);
        setCustomVenueName('');
      }
    } else if (onSystemClick) {
      onSystemClick();
    }
  };

  // Handle "Add venue" click
  const handleAddVenueClick = (e) => {
    e.stopPropagation();
    setIsAddingCustom(true);
    setCustomVenueName('');
  };

  // Handle save custom venue
  const handleSaveCustomVenue = (e) => {
    e.stopPropagation();
    if (customVenueName.trim()) {
      const customVenue = {
        name: customVenueName.trim(),
        category: null,
        chain: null,
        isCustom: true,
      };

      // Save to localStorage for future lookups at this location
      if (coords.latitude && coords.longitude) {
        saveCustomVenue(customVenueName.trim(), coords.latitude, coords.longitude);
      }

      if (onVenueChange) {
        onVenueChange(customVenue);
      }
      setIsAddingCustom(false);
      setCustomVenueName('');
      setIsExpanded(false);
      if (onExpandChange) onExpandChange(false);
    }
  };

  // Handle keyboard in custom venue input
  const handleCustomInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveCustomVenue(e);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // Close dropdown (same as tapping outside)
      setIsExpanded(false);
      setIsAddingCustom(false);
      setCustomVenueName('');
    }
  };

  // Handle venue selection
  const handleVenueSelect = (venue, e) => {
    e.stopPropagation();
    if (!keepOpenOnSelect) {
      setIsExpanded(false);
      if (onExpandChange) onExpandChange(false);
    }
    if (onVenueChange) {
      onVenueChange(venue);
    }
  };

  // Check if a venue is currently selected
  const isVenueSelected = (venue) => {
    if (!currentVenue && !venue) return true; // "No venue" selected
    if (!currentVenue || !venue) return false;
    return currentVenue.name === venue.name;
  };

  // Close dropdown when tapping outside
  const handleBackdropClick = () => {
    setIsExpanded(false);
    if (onExpandChange) onExpandChange(false);
    setIsAddingCustom(false);
    setCustomVenueName('');
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        // Left mode (detail page) lets the page's flex gap own the spacing — no trailing margin.
        marginBottom: isLeft ? '0' : '14px',
        marginTop: '0',
        width: '100%',
        // Left mode (detail page): no outer inset — the card wrapper + pill padding already
        // place content at 16px, matching the ABOUT card. Chat keeps its 16px inset.
        padding: isLeft ? '0' : '0 16px',
      }}
    >
      {/* Invisible backdrop to close dropdown when tapping outside (not used with inlineDropdown) */}
      {isExpanded && !inlineDropdown && (
        <div
          onClick={handleBackdropClick}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99, // Below dropdown (100) but above everything else
          }}
        />
      )}
      <div
        onClick={handlePillClick}
        style={{
          ...TYPOGRAPHY.BODY_REGULAR,
          color: '#666',
          textAlign: contentTextAlign,
          backgroundColor: backgroundColor,
          borderRadius:
            isExpanded && !inlineDropdown
              ? dropUp
                ? '0 0 20px 20px' // Dropdown opens above
                : '20px 20px 0 0' // Dropdown opens below
              : '20px',
          padding: flush ? '0 16px' : '16px',
          cursor: canExpand ? 'pointer' : onSystemClick ? 'pointer' : 'default',
          position: 'relative',
          width: '100%',
          maxWidth: `${maxWidth}px`,
        }}
      >
        {/* Caret positioned at far right of container, not affecting text centering */}
        {showChevron && (
          <CaretDown
            size={18}
            color="#999"
            weight="bold"
            style={{
              position: 'absolute',
              right: '16px',
              top: '50%',
              transform: dropUp
                ? isExpanded
                  ? 'translateY(-50%) rotate(0deg)'
                  : 'translateY(-50%) rotate(180deg)'
                : isExpanded
                  ? 'translateY(-50%) rotate(180deg)'
                  : 'translateY(-50%) rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          />
        )}

        {/* Location display - venue (darker) on own line, address (lighter) below */}
        {/* For Anon view: city-only for privacy */}
        <div style={{ textAlign: contentTextAlign, width: '100%' }}>
          {isAnonView ? (
            /* Anon view: city-only display for privacy. No location (denied
               permission / GPS failure) → time-only; this section is the slot a
               later Profile-added place syncs into via metadata_updated. */
            formatLocationForAnon(connectionMetadata) && (
              <div style={primaryLineStyle}>
                {placePin}
                {formatLocationForAnon(connectionMetadata)}
              </div>
            )
          ) : (
            /* Profile view: full venue + address display */
            <>
              {/* Primary place line — venue name, or the chat-pill place for
                  no-venue cards (structural mirror, one block) */}
              {primaryPlace && (
                <div style={primaryLineStyle}>
                  {placePin}
                  {primaryPlace}
                </div>
              )}
              {/* Address line: subordinate under a primary place line (the no-venue
                  case strips whatever component the primary shows); primary itself
                  only in the degraded no-place fallback */}
              {(() => {
                if (!showAddress || !subordinate) return null;
                const text = subordinate;
                const isSub = !!primaryPlace;
                return (
                  <div
                    style={{
                      marginTop: isSub ? '2px' : '0',
                      // Address lighter than datetime when subordinate, same as venue when primary
                      fontWeight: isSub ? '400' : '500',
                      color: isSub ? '#B0B0B0' : '#333',
                      // Detail page: subordinate line 14px; primary matches the 16px venue/date size
                      fontSize: isLeft ? (isSub ? '14px' : '16px') : '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: contentJustify,
                      gap: '4px',
                    }}
                  >
                    {!isSub && placePin}
                    {text}
                  </div>
                );
              })()}
              {!currentVenue?.name && !fullAddress && connectionMetadata?.location && (
                <span style={{ ...primaryLineStyle, display: 'inline-flex' }}>
                  {placePin}
                  {connectionMetadata.location.split('\n')[0]}
                </span>
              )}
              {!currentVenue?.name &&
                !fullAddress &&
                !connectionMetadata?.location &&
                (isEditable || showAddPlaceInvite) && (
                  /* No location on the connection (denied permission, GPS
                     failure, or middle-of-nowhere): invite manual recovery —
                     inline pills expand the picker on tap; detail cards set
                     showAddPlaceInvite and their wrapper tap opens the overlay.
                     Read-only views render nothing; the date line still shows.
                     Placeholder grey, not a colored action — the words do the
                     work, matching the article-less add-prompt family
                     ("Add group" / "Add contact" / "Add name"). */
                  <span style={{ ...primaryLineStyle, color: '#8E8E93', display: 'inline-flex' }}>
                    {placePin}
                    Add place
                  </span>
                )}
            </>
          )}
        </div>

        {/* Date/time line (below location) */}
        {showDateTime && dateTimeLine && (
          <div style={{ marginTop: '6px', fontSize: '16px' }}>{dateTimeLine}</div>
        )}

        {/* Inline dropdown (expands within the pill) */}
        {isExpanded && isEditable && (
          <div
            style={{
              ...(inlineDropdown
                ? {
                    // Flow-based: dropdown is part of the pill, no overlap
                    marginTop: '4px',
                  }
                : {
                    // Absolute: dropdown floats outside the pill
                    position: 'absolute',
                    ...(dropUp ? { bottom: '100%' } : { top: '100%' }),
                    left: 0,
                    right: 0,
                    zIndex: 100,
                  }),
              backgroundColor: backgroundColor,
              borderRadius: inlineDropdown
                ? '0'
                : dropUp
                  ? `${BORDER_RADIUS.PILL} ${BORDER_RADIUS.PILL} 0 0`
                  : `0 0 ${BORDER_RADIUS.PILL} ${BORDER_RADIUS.PILL}`,
              ...(dropUp
                ? { borderBottom: '1px solid #E5E5E5' }
                : { borderTop: '1px solid #E5E5E5' }),
              overflow: 'hidden',
            }}
          >
            {/* Scrollable venue list */}
            <div
              style={{
                maxHeight: `${maxDropdownHeight}px`,
                overflowY: 'auto',
              }}
            >
              {/* "No venue" option - first when there is an address to fall back to
                  (with no address the label would lie — the picker then only offers
                  nearby venues and the add input) */}
              {fullAddress && (
                <VenueOption
                  label={<em>Just address</em>}
                  isSelected={!currentVenue}
                  onClick={(e) => handleVenueSelect(null, e)}
                />
              )}

              {/* Nearby venues */}
              {nearbyVenues
                .filter((v) => !deletedVenues.includes(v.name.toLowerCase()))
                .map((venue, index) => (
                  <VenueOption
                    key={venue.name || index}
                    label={venue.name}
                    isSelected={isVenueSelected(venue)}
                    isCustom={venue.isCustom}
                    onClick={(e) => handleVenueSelect(venue, e)}
                    onDelete={
                      venue.isCustom ? () => handleDeleteCustomVenue(venue.name) : undefined
                    }
                  />
                ))}
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid #E5E5E5' }} />

            {/* Add venue option or input - always visible at bottom */}
            {isAddingCustom ? (
              <AddVenueInput
                value={customVenueName}
                onChange={setCustomVenueName}
                onSave={handleSaveCustomVenue}
                onKeyDown={handleCustomInputKeyDown}
              />
            ) : (
              <div
                onClick={handleAddVenueClick}
                style={{
                  padding: '12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  color: '#007AFF',
                  fontWeight: '500',
                }}
              >
                <span style={{ marginRight: '6px' }}>+</span>
                <span>Add venue</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * VenueOption - Individual option in the dropdown
 */
const VenueOption = ({ label, isSelected, isCustom, onClick, onDelete }) => (
  <div
    onClick={onClick}
    style={{
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      backgroundColor: isSelected ? '#F0F0F5' : 'transparent',
      transition: 'background-color 0.15s ease',
    }}
  >
    <span style={{ color: '#333', flex: 1 }}>{label}</span>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      {isSelected && <span style={{ color: '#007AFF', fontWeight: '500' }}>✓</span>}
      {isCustom && onDelete && (
        <X
          size={16}
          color="#999"
          weight="bold"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          style={{ cursor: 'pointer' }}
          aria-label="Delete custom venue"
        />
      )}
    </div>
  </div>
);

VenueOption.propTypes = {
  label: PropTypes.node.isRequired,
  isSelected: PropTypes.bool,
  isCustom: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
};

/**
 * AddVenueInput - Input field with inline checkmark button for typing custom venue name
 */
const AddVenueInput = ({ value, onChange, onSave, onKeyDown }) => (
  <div
    style={{
      padding: '12px 16px',
    }}
    onClick={(e) => e.stopPropagation()}
  >
    <div
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Venue name"
        autoFocus
        style={{
          width: '100%',
          padding: '10px 40px 10px 12px', // Extra right padding for checkmark button
          border: '1px solid #E5E5E5',
          borderRadius: '8px',
          fontSize: '14px',
          boxSizing: 'border-box',
        }}
      />
      {/* Inline checkmark button */}
      <button
        type="button"
        onClick={onSave}
        disabled={!value.trim()}
        style={{
          position: 'absolute',
          right: '4px',
          width: '32px',
          height: '32px',
          border: 'none',
          borderRadius: '6px',
          background: value.trim() ? '#007AFF' : '#E5E5E5',
          color: value.trim() ? 'white' : '#999',
          cursor: value.trim() ? 'pointer' : 'default',
          fontSize: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 0,
        }}
        aria-label="Save venue"
      >
        ✓
      </button>
    </div>
  </div>
);

AddVenueInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
};

VenueSelectorMessage.propTypes = {
  connectionMetadata: PropTypes.shape({
    location: PropTypes.string,
    locationData: PropTypes.shape({
      venueInfo: PropTypes.shape({
        venue: PropTypes.shape({
          name: PropTypes.string,
          category: PropTypes.string,
          chain: PropTypes.string,
          isCustom: PropTypes.bool, // true for manually-entered venues
        }),
        nearbyVenues: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            category: PropTypes.string,
            chain: PropTypes.string,
          })
        ),
        address: PropTypes.object,
      }),
    }),
    meetingDate: PropTypes.string,
  }),
  isEditable: PropTypes.bool,
  onVenueChange: PropTypes.func,
  onSystemClick: PropTypes.func,
  onExpandChange: PropTypes.func,
  dropDirection: PropTypes.oneOf(['up', 'down']),
  backgroundColor: PropTypes.string,
  maxDropdownHeight: PropTypes.number,
  maxWidth: PropTypes.number,
  keepOpenOnSelect: PropTypes.bool,
  showDateTime: PropTypes.bool,
  inlineDropdown: PropTypes.bool,
  isAnonView: PropTypes.bool,
  friendlyTime: PropTypes.bool,
  showAddress: PropTypes.bool,
  showPin: PropTypes.bool,
  composePlace: PropTypes.bool,
  primaryTextStyle: PropTypes.object,
  align: PropTypes.oneOf(['left', 'center']),
  flush: PropTypes.bool,
};

export default VenueSelectorMessage;
